import type {
    IrrigationDevice,
    IrrigationRun,
} from '@/lib/api/irrigation-devices-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    useCancelIrrigationRun,
    useIrrigationRuns,
} from '@/hooks/use-irrigation-schedule-query'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { formatTimeVN } from '@/lib/irrigation-schedule'
import { m } from '@/lib/paraglide/messages'

const STATUS_LABEL: Record<IrrigationRun['status'], () => string> = {
    planned: m.irrigation_run_status_planned,
    starting: m.irrigation_run_status_starting,
    running: m.irrigation_run_status_running,
    done: m.irrigation_run_status_done,
    skipped: m.irrigation_run_status_skipped,
    cancelled: m.irrigation_run_status_cancelled,
}

const STATUS_VARIANT: Record<
    IrrigationRun['status'],
    'default' | 'secondary' | 'destructive' | 'outline'
> = {
    planned: 'outline',
    starting: 'default',
    running: 'default',
    done: 'secondary',
    skipped: 'destructive',
    cancelled: 'secondary',
}

const SOURCE_LABEL: Record<IrrigationRun['source'], () => string> = {
    now: m.irrigation_run_source_now,
    once: m.irrigation_run_source_once,
    schedule: m.irrigation_run_source_schedule,
}

/** Phiên đang chạy, sắp chạy và mới xong của một bộ tưới. */
export function IrrigationRunsList({
    device,
    canEdit,
    enabled,
}: {
    device: IrrigationDevice
    canEdit: boolean
    enabled: boolean
}) {
    const { data: runs = [], isError } = useIrrigationRuns(device.id, enabled)
    const cancel = useCancelIrrigationRun(device.id)

    return (
        <section className="space-y-2" aria-label={m.irrigation_runs_title()}>
            <h3 className="text-sm font-medium">{m.irrigation_runs_title()}</h3>
            {isError ? (
                <p className="text-sm text-destructive">
                    {m.irrigation_runs_load_error()}
                </p>
            ) : runs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    {m.irrigation_runs_empty()}
                </p>
            ) : (
                <ul className="divide-y rounded-md border">
                    {runs.map((run) => (
                        <li
                            key={run.id}
                            className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                        >
                            <span className="tabular-nums font-medium">
                                {formatTimeVN(run.planned_start, true)}–
                                {formatTimeVN(run.planned_end)}
                            </span>
                            <span className="text-muted-foreground">
                                {SOURCE_LABEL[run.source]()}
                            </span>
                            <Badge variant={STATUS_VARIANT[run.status]}>
                                {STATUS_LABEL[run.status]()}
                            </Badge>
                            {run.status === 'skipped' && run.skip_reason && (
                                <span className="w-full text-xs text-destructive">
                                    {resolveApiErrorMessage(run.skip_reason) ??
                                        run.skip_reason}
                                </span>
                            )}
                            {run.off_attempts > 0 &&
                                run.status === 'running' && (
                                    <span className="w-full text-xs text-destructive">
                                        {m.irrigation_run_off_retry({
                                            count: run.off_attempts,
                                        })}
                                    </span>
                                )}
                            {canEdit && run.status === 'planned' && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="ml-auto"
                                    disabled={cancel.isPending}
                                    onClick={() => cancel.mutate(run.id)}
                                >
                                    {m.irrigation_run_cancel()}
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}
